/**
 * Firebase Auth over its REST endpoints rather than the SDK.
 *
 * The head's promise is that it ships everything it runs, and `tools/pair.html`
 * already proved the whole flow works with plain fetch. The SDK would buy
 * provider popups — which this origin's `Cross-Origin-Opener-Policy` breaks
 * anyway — in exchange for a dependency whose default distribution is a CDN.
 *
 * The API key is public by design: it identifies the project to Google's
 * endpoints and authorises nothing on its own.
 */

export const API_KEY = 'AIzaSyAFjYE6NSJf8QymPjoWHQy_YDGwOldmYO8';
export const IDENTITY = 'https://identitytoolkit.googleapis.com/v1';
const SECURE_TOKEN = 'https://securetoken.googleapis.com/v1';

/** Refresh a minute early: a token that expires mid-flight reads to the
 *  caller exactly like a revoked grant, and those must not be confusable. */
const REFRESH_SKEW_MS = 60_000;

export interface AuthSession {
  idToken: string;
  refreshToken: string;
  expiresAtMs: number;
  email: string;
  uid: string;
}

export class AuthError extends Error {}

export async function signInWithPassword(email: string, password: string): Promise<AuthSession> {
  const body = await post(`${IDENTITY}/accounts:signInWithPassword?key=${API_KEY}`, {
    email,
    password,
    returnSecureToken: true,
  });
  return {
    idToken: body.idToken as string,
    refreshToken: body.refreshToken as string,
    expiresAtMs: Date.now() + Number(body.expiresIn ?? 3600) * 1000,
    email: (body.email as string) ?? email,
    uid: body.localId as string,
  };
}

export async function refresh(session: AuthSession): Promise<AuthSession> {
  const body = await post(`${SECURE_TOKEN}/token?key=${API_KEY}`, {
    grant_type: 'refresh_token',
    refresh_token: session.refreshToken,
  });
  return {
    ...session,
    idToken: body.id_token as string,
    refreshToken: (body.refresh_token as string) ?? session.refreshToken,
    expiresAtMs: Date.now() + Number(body.expires_in ?? 3600) * 1000,
  };
}

export function isExpired(session: AuthSession): boolean {
  return Date.now() >= session.expiresAtMs - REFRESH_SKEW_MS;
}

/**
 * A live session from a stored refresh token alone — what a cold load has to
 * work from, since nothing else survives it.
 *
 * The address and the uid are read back out of the returned id_token rather
 * than stored beside the token. That keeps what is written to disk down to the
 * one credential, and it cannot go stale against the account the way a
 * remembered copy of the address could.
 *
 * Throws like any other sign-in when the token is refused, which is the caller's
 * signal that this browser has been cut off and the stored copy is rubbish.
 */
export async function resumeFromRefreshToken(refreshToken: string): Promise<AuthSession> {
  const body = await post(`${SECURE_TOKEN}/token?key=${API_KEY}`, {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });
  const idToken = body.id_token as string;
  const claims = claimsIn(idToken);
  return {
    idToken,
    refreshToken: (body.refresh_token as string) ?? refreshToken,
    expiresAtMs: Date.now() + Number(body.expires_in ?? 3600) * 1000,
    email: claims?.email ?? '',
    uid: (body.user_id as string) ?? claims?.user_id ?? '',
  };
}

/** The unverified payload half of a JWT. Read only for the address to show and
 *  the uid to key on — the token itself is what the backend verifies. */
function claimsIn(idToken: string): { email?: string; user_id?: string } | null {
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    return JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/')));
  } catch {
    return null;
  }
}

async function post(url: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok || !body) {
    const message = (body?.['error'] as { message?: string } | undefined)?.message ?? 'sign-in failed';
    throw new AuthError(readableAuthError(message));
  }
  return body;
}

/** Google's codes are shouty constants; the ones a person can act on get
 *  said plainly and the rest stay generic rather than guessing. */
function readableAuthError(code: string): string {
  if (code.startsWith('INVALID_LOGIN_CREDENTIALS') || code.startsWith('INVALID_PASSWORD')) {
    return 'That email and password did not match.';
  }
  if (code.startsWith('EMAIL_NOT_FOUND')) return 'No account with that email.';
  if (code.startsWith('TOO_MANY_ATTEMPTS')) return 'Too many attempts — wait a moment and try again.';
  if (code.startsWith('USER_DISABLED')) return 'That account is disabled.';
  return 'Could not sign in.';
}
